// Contact.jsx
import React, { useEffect } from "react";
import "./Contact.css";
import { useScrollAnimation } from "../../hooks/useScrollAnimation";
import Header from "../../components/Header/Header";
import Footer from "../../components/Footer/Footer";
import {
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaClock,
  FaPaperPlane,
  FaMap,
  FaQuestionCircle,
} from "react-icons/fa";
import { Helmet } from "react-helmet-async";

// Import your images
import contactHero from "../../assets/images/contact-hero.jpg";
import mapImage from "../../assets/images/map-placeholder.jpg";

const Contact = () => {
  useScrollAnimation();

  useEffect(() => {
    // Add animation classes to elements on load
    const animateElements = document.querySelectorAll(".animate-on-load");
    animateElements.forEach((element, index) => {
      element.style.animationDelay = `${0.2 + index * 0.1}s`;
    });
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle form submission here
    console.log("Form submitted");
  };

  return (
    <div>
      <Helmet>
        <title>
          Contact Us | Avvoka - Exporters of Fresh Produce, Tea & Coffee
        </title>
        <meta
          name="description"
          content="Get in touch with Avvoka for export inquiries, partnerships, and customer support. Call us at +254 727 834 517 or email info@avvoka.co.ke."
        />
        <meta
          name="keywords"
          content="contact Avvoka, export inquiries, Kenyan fresh produce, tea exporters, coffee exporters, global trade, Kenya export company"
        />
        <meta
          property="og:title"
          content="Contact Avvoka | Exporters of Fresh Produce, Tea & Coffee"
        />
        <meta
          property="og:description"
          content="Reach Avvoka for fresh produce, tea, and coffee export inquiries. Connect with us by phone or email for reliable global trade solutions."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://avvoka.co.ke/contact" />
        <meta
          property="og:image"
          content="https://avvoka.co.ke/assets/images/contact-og-image.jpg"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@AvvokaExports" />
        <meta
          name="theme-color"
          content="#4CAF50"
          media="(prefers-color-scheme: light)"
        />
        <meta
          name="theme-color"
          content="#2E7D32"
          media="(prefers-color-scheme: dark)"
        />
      </Helmet>

      <Header />
      <div className="contact-page">
        {/* Hero Section */}
        <section className="contact-hero">
          <div className="hero-background">
            <div className="parallax-img contact-img-1"></div>
            <div className="overlay"></div>
          </div>
          <div className="container">
            <div className="hero-content">
              <h1 className="hero-title animate-on-load">
                Get in <span className="text-accent">Touch</span>
              </h1>
              <p className="hero-subtitle animate-on-load">
                We'd love to hear from you. Let's start a conversation.
              </p>
            </div>
          </div>
        </section>

        {/* Contact Info Section */}
        <section className="contact-info-section">
          <div className="container">
            <div className="section-header">
              <h2 className="animate-on-scroll">How Can We Help?</h2>
              <p className="animate-on-scroll delay-1">
                Reach out to us through any of these channels
              </p>
            </div>

            <div className="contact-grid">
              <div className="contact-card animate-on-scroll">
                <div className="contact-icon">
                  <div className="icon-circle">
                    <FaEnvelope />
                  </div>
                </div>
                <h3>Email Us</h3>
                <p>info@avvokaexports.com</p>
                <p>sales@avvokaexports.com</p>
                <span className="contact-response">
                  Response within 24 hours
                </span>
              </div>

              <div className="contact-card animate-on-scroll delay-1">
                <div className="contact-icon">
                  <div className="icon-circle">
                    <FaPhone />
                  </div>
                </div>
                <h3>Call Us</h3>
                <p>+254 727 834 517</p>
                <p>+254 720 987 654</p>
                <span className="contact-response">Mon-Fri, 8AM-5PM EAT</span>
              </div>

              <div className="contact-card animate-on-scroll delay-2">
                <div className="contact-icon">
                  <div className="icon-circle">
                    <FaMapMarkerAlt />
                  </div>
                </div>
                <h3>Visit Us</h3>
                <p>Avvoka Exports Center</p>
                <p>Nairobi, Kenya</p>
                <span className="contact-response">By appointment only</span>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Form & Map Section */}
        <section className="contact-form-section">
          <div className="container">
            <div className="form-map-grid">
              <div className="form-container animate-left">
                <h2>Send Us a Message</h2>
                <form className="contact-form" onSubmit={handleSubmit}>
                  <div className="form-group">
                    <label htmlFor="name">Full Name</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      placeholder="Your full name"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">Email Address</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      placeholder="Your email address"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone">Phone Number</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      placeholder="Your phone number"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="subject">Subject</label>
                    <select id="subject" name="subject" required>
                      <option value="">Select a subject</option>
                      <option value="general">General Inquiry</option>
                      <option value="quote">Request a Quote</option>
                      <option value="partnership">
                        Partnership Opportunity
                      </option>
                      <option value="support">Customer Support</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="message">Message</label>
                    <textarea
                      id="message"
                      name="message"
                      rows="5"
                      placeholder="How can we help you?"
                      required
                    ></textarea>
                  </div>

                  <button type="submit" className="btn btn-primary">
                    <FaPaperPlane style={{ marginRight: "8px" }} />
                    Send Message
                  </button>
                </form>
              </div>

              <div className="map-container animate-right">
                <h2>Our Location</h2>
                <div className="map-placeholder">
                  <img src={mapImage} alt="Avvoka Exports location map" />
                  <div className="map-overlay">
                    <button className="btn btn-secondary">
                      <FaMap style={{ marginRight: "8px" }} />
                      View on Google Maps
                    </button>
                  </div>
                </div>

                <div className="office-hours">
                  <h3>
                    <FaClock style={{ marginRight: "10px" }} />
                    Office Hours
                  </h3>
                  <ul>
                    <li>
                      <span>Monday - Friday:</span> 8:00 AM - 5:00 PM
                    </li>
                    <li>
                      <span>Saturday:</span> 9:00 AM - 1:00 PM
                    </li>
                    <li>
                      <span>Sunday:</span> Closed
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="faq-section">
          <div className="container">
            <div className="section-header">
              <h2 className="animate-on-scroll">
                <FaQuestionCircle style={{ marginRight: "10px" }} />
                Frequently Asked Questions
              </h2>
              <p className="animate-on-scroll delay-1">
                Quick answers to common questions
              </p>
            </div>

            <div className="faq-grid">
              <div className="faq-item animate-on-scroll">
                <h3>What are your minimum order requirements?</h3>
                <p>
                  Minimum orders vary by product. For most agricultural exports,
                  we require a minimum of one pallet or container, depending on
                  the product type and destination.
                </p>
              </div>

              <div className="faq-item animate-on-scroll delay-1">
                <h3>Do you offer samples before bulk orders?</h3>
                <p>
                  Yes, we provide samples for most products. Sample costs vary
                  and are typically credited against future bulk orders.
                </p>
              </div>

              <div className="faq-item animate-on-scroll delay-2">
                <h3>What shipping methods do you use?</h3>
                <p>
                  We use air freight for perishable items and sea freight for
                  non-perishable bulk orders. We select the most efficient and
                  cost-effective method for each shipment.
                </p>
              </div>

              <div className="faq-item animate-on-scroll delay-3">
                <h3>How do you ensure product quality during transit?</h3>
                <p>
                  We use specialized packaging, temperature-controlled
                  containers when needed, and work with reputable logistics
                  partners to maintain product quality throughout the shipping
                  process.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="contact-cta">
          <div className="container">
            <div className="cta-content">
              <h2 className="animate-right">Ready to Partner With Us?</h2>
              <p className="animate-left">
                Join our network of international clients experiencing the
                finest Kenyan agricultural products.
              </p>
              <div className="cta-buttons animate-on-scroll delay-1">
                <button className="btn btn-primary">Request a Quote</button>
                <button className="btn btn-secondary">Browse Products</button>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </div>
  );
};

export default Contact;
